import React from 'react';
import { Typography, Button, Link, Divider, IconButton, Tooltip } from '@mui/material';
import RemoveRedEye from '@mui/icons-material/RemoveRedEye';
import { Grid } from '@mui/system';
import { Form } from 'formik';

import { MuiForm } from 'lib/Form';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { Location } from 'components/location/models/LocationModel';
import { Row } from 'ui/Flex';
import { Panel } from 'ui/Panel';
import { LocationAlternativeOpeningHoursModal } from 'components/location/modal/LocationAlternativeOpeningHoursModal';
import { locationApi } from 'components/location/LocationApi';

interface HTMLOpeningHoursFormValues {
    alternativeOpeningHoursText: string;
}

export function LegacyCustomOpeningHours({
    currentLocation,
    setCurrentLocation
}: {
    currentLocation: Location;
    setCurrentLocation: (location: Location) => void;
}) {
    const [isEditing, setIsEditing] = React.useState(false);
    const [openAlternativeHoursModal, setOpenAlternativeHoursModal] = React.useState(false);
    const htmlInitialValues: HTMLOpeningHoursFormValues = React.useMemo(
        () => ({
            alternativeOpeningHoursText: currentLocation.alternativeOpeningHoursText ?? ''
        }),
        [currentLocation.alternativeOpeningHoursText]
    );
    const handleHTMLSubmit = React.useCallback(
        async (values: HTMLOpeningHoursFormValues) => {
            const locationId = currentLocation._id;
            const body = {
                alternativeOpeningHoursText: values.alternativeOpeningHoursText
            };

            const response = await locationApi.update(locationId, { body });
            if (response.ok) {
                setCurrentLocation(response.body);
            }
            setIsEditing(null);
        },
        [currentLocation._id, setCurrentLocation]
    );
    const toggleEditing = React.useCallback(() => {
        setIsEditing(prev => !prev);
    }, []);
    const toggleView = React.useCallback(() => {
        setOpenAlternativeHoursModal(prev => !prev);
    }, []);
    return (
        <>
            <Panel
                title="Legacy Custom Opening Hours"
                showAction={!isEditing}
                onClick={toggleEditing}
                divider={isEditing}
            >
                {isEditing ? (
                    <MuiForm<HTMLOpeningHoursFormValues>
                        initialValues={htmlInitialValues}
                        onSubmit={handleHTMLSubmit}
                        enableReinitialize
                    >
                        {({ submitForm, isSubmitting }) => (
                            <Form>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <TextFormField
                                            name="alternativeOpeningHoursText"
                                            label="Legacy Custom Opening Hours HTML"
                                            multiline
                                            rows={4}
                                        />
                                        <Typography variant="caption">
                                            Important: Html will always override the new advanced custom
                                            opening hours feature. If you want to use the new advanced custom
                                            opening hours feature, you need to migrate away from using html.
                                            Please{' '}
                                            <Link href="mailto:support@pepperhq.com" target="_blank">
                                                click here
                                            </Link>{' '}
                                            to speak to a member of the team.
                                        </Typography>
                                    </Grid>
                                    <Grid size={12}>
                                        <Row gutter align="flex-end">
                                            <Button onClick={toggleEditing} variant="outlined">
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={submitForm}
                                                disabled={isSubmitting}
                                                variant="contained"
                                                color="primary"
                                            >
                                                Submit
                                            </Button>
                                        </Row>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </MuiForm>
                ) : (
                    <Tooltip
                        placement="left"
                        title="Using HTML, This lets you override how opening hours are displayed in the app.
                     However, the standard opening hours will still be used to determine when the location is open for ordering.
                     This will replace your standard defined opening hours."
                    >
                        <Grid size={12}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid size={12}>
                                    <Divider style={{ opacity: 0.4 }} />
                                </Grid>
                                <Grid size={12}>
                                    <Typography>
                                        <b>Custom Opening Hours HTML</b>
                                    </Typography>
                                </Grid>
                                <Grid size={8}>
                                    <Row valign="center">
                                        <Typography
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {currentLocation.alternativeOpeningHoursText}
                                        </Typography>
                                        <Tooltip title="View">
                                            <IconButton onClick={toggleView} size="large">
                                                <RemoveRedEye />
                                            </IconButton>
                                        </Tooltip>
                                    </Row>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Tooltip>
                )}
            </Panel>
            <LocationAlternativeOpeningHoursModal
                open={openAlternativeHoursModal}
                onClose={toggleView}
                openingHours={currentLocation.alternativeOpeningHoursText}
            />
        </>
    );
}
